import {action, makeAutoObservable, observable} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {isEmpty, reduce} from "lodash";
import {ChecksumsKeys} from "data/enums";

export type IChecksums = Partial<Record<ChecksumsKeys, string>>;

export interface IChecksumStore {
	get checksums(): IChecksums;
	get changedChecksums(): IChecksums;
	get subscriptions(): Record<string, (() => Promise<void>)[]>;

	fetchChecksums(): Promise<void>;
	get liveContestId(): number;
	get liveRoundId(): number;
	setLiveContestId(contestId: number): void;
	setLiveRoundId(roundId: number): void;

	addCallback: (key: ChecksumsKeys, callback: () => Promise<void>) => () => void;
	removeCallback: (key: ChecksumsKeys, callback?: () => Promise<void>) => void;
}

@injectable()
export class ChecksumStore implements IChecksumStore {
	@observable _liveContestId: number = 0;
	@observable _liveRoundId: number = 0;
	@observable protected _checksums: IChecksums = {};
	@observable _changedChecksums: IChecksums = {};
	@observable _callbacks: Record<string, (() => Promise<void>)[]> = {};

	get checksums() {
		return this._checksums;
	}

	get changedChecksums() {
		return this._changedChecksums;
	}

	get subscriptions(): Record<string, (() => Promise<void>)[]> {
		return this._callbacks;
	}

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	@action
	async fetchChecksums(): Promise<void> {
		try {
			const {data} = await this._jsonProvider.checksums();

			if (isEmpty(this.checksums)) {
				this._checksums = data;
			} else {
				this.compareChecksums(data);
			}
		} catch (e) {
			console.error("Error while fetching checksums");
		}
	}

	@action
	protected compareChecksums(checksums: IChecksums): void {
		this._changedChecksums = reduce(
			checksums,
			(acc, checksum, key: string) => {
				const oldChecksum = this._checksums[key as ChecksumsKeys];
				return checksum !== oldChecksum ? {...acc, [key]: checksum} : acc;
			},
			{}
		);

		this._checksums = checksums;
	}

	get liveContestId(): number {
		return this._liveContestId;
	}

	get liveRoundId(): number {
		return this._liveRoundId;
	}

	setLiveContestId(contestId: number) {
		this._liveContestId = contestId;
	}

	setLiveRoundId(roundId: number) {
		this._liveRoundId = roundId;
	}

	addCallback = (key: ChecksumsKeys, callback: () => Promise<void>) => {
		if (!this._callbacks[key]) {
			this._callbacks[key] = [];
		}

		this._callbacks[key].push(callback);

		return () => this.removeCallback(key, callback);
	};

	removeCallback = (key: ChecksumsKeys, callback?: () => Promise<void>) => {
		if (!this._callbacks[key]) {
			this._callbacks[key] = [];
		}

		if (callback) {
			this._callbacks[key] = this._callbacks[key].filter((it) => it !== callback);
		}
	};
}
